// MessageInput.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import Trans from '../component/Trans.jsx';

import TextEditor from '../container/TextEditor.js';
import Spinning from '../style/Spinning.js';
import { textColor } from '../style/variables.js';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';

import GiftIcon from '../../img/ic_gift_fill.svg';
import AudioIcon from '../../img/ic_record.svg';
import SendIcon from '../../img/chatroom_send.svg';
import DiamondImage from '../../img/point_2.svg';
import RemoveIcon from '../../img/ic_close_gray.svg';
import LoadingImage from '../../img/loading.png';
import CameraIcon from '../../img/icon-camera-fill-shadow.svg';
import { ButtonId } from '../resource/mixpanel.js';
import { matchFilterWords } from '../resource/wordsFilter.js';
import getMediaBlob from '../resource/getMediaBlob.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { QUICKPAY_BUNDLE_SECTION } from '../resource/shopConstants.js';

const MESSAGE_TEXT_LIMIT = 600; // TODO: remote config

export class MessageInput extends React.PureComponent {
  chatroomId = undefined;
  hasShownCameraPromptNotification = false;

  // request camera permission
  requestWebappCameraPermission = async () => {
    try {
      const permissionStatus = await window.WebApp.callHandler(
        'requestCameraPermission'
      );
      return permissionStatus;
    } catch (_) {
      // if failed, return prompt
      return 'prompt';
    }
  };

  componentDidMount() {
    const { setMessageInputHeight } = this.props;
    const pathname = window.location.pathname;
    const isOnChatroom = /chat\/.+/.test(window.location.pathname);
    if (isOnChatroom) {
      this.chatroomId = pathname.replace('/chat/', '');
    }
    setMessageInputHeight({
      height: this.messageInputRef?.clientHeight,
      chatroomId: this.chatroomId,
    });
    window.addEventListener('resize', () => {
      this.setMessageInputHeightDebounce();
    });
  }

  componentDidUpdate(prevProps) {
    const { text, media } = this.props;

    if (text !== prevProps.text || media !== prevProps.media) {
      this.setMessageInputHeightDebounce();
    }
  }

  componentWillUnmount() {
    this.props.setInputFocused(false);
    window.removeEventListener('resize', () => {
      this.setMessageInputHeightDebounce();
    });
    this.mediaPreviewSrc && URL.revokeObjectURL(this.mediaPreviewSrc);
  }
  renderPlaceholder() {
    const { cost, text, type, isFocused, t } = this.props;
    const focused = text || isFocused;
    const replyTextColor = cost > 0 && focused ? '#c8c8c8' : '#fff';
    // Check with '!cost' instead of 'cost === 0' since Server might pass cost = null from API.
    if (!cost && focused) return null;

    return (
      <Placeholder
        textColor={type === 'chatroom' ? '#828282' : replyTextColor}
        isFocused={focused}
      >
        {cost > 0 ? (
          <Trans
            defaults={t('chat_input_placeholder', {
              price: cost,
              ns: TranslationNamespace.CHAT,
            })
              .replace('#', '<0></0>')
              .replace(cost, '<1>{cost}</1>')}
            values={{ cost }}
            components={[
              <Diamond key="DiamondIcon" src={DiamondImage} alt="diamond" />,
              <ReplayPrice key="ReplayPrice">{cost}</ReplayPrice>,
            ]}
          />
        ) : (
          t('chat_free_reply_input_placeholder', {
            ns: TranslationNamespace.CHAT,
          })
        )}
      </Placeholder>
    );
  }

  renderMediaPreview() {
    const { media, onChange } = this.props;
    const isImage = /^image\//i.test(media.type);
    const isVideo = /^video\//i.test(media.type);
    this.mediaPreviewSrc = URL.createObjectURL(media.fileBlob);
    return (
      <MediaPreview>
        <Media>
          <MediaRemover
            onMouseDown={e => e.preventDefault()}
            onClick={() => {
              this.mediaPreviewSrc = URL.revokeObjectURL(this.mediaPreviewSrc);
              this.editor.blur();
              onChange({ media: null });
            }}
          >
            <img src={RemoveIcon} alt="remove" />
          </MediaRemover>
          <PreviewWrapper>
            {isImage ? (
              <Preview as="img" src={this.mediaPreviewSrc} />
            ) : isVideo ? (
              <Preview
                as="video"
                src={this.mediaPreviewSrc}
                playsInline
                autoPlay
                muted
              />
            ) : (
              <Loading src={LoadingImage} alt="loading" />
            )}
          </PreviewWrapper>
        </Media>
      </MediaPreview>
    );
  }

  renderTextPreview() {
    const { text, isFocused } = this.props;
    return text.length && !isFocused ? <TextPreview>{text}</TextPreview> : null;
  }

  renderCameraButton() {
    const { hasCamera, canCreateMessage, isCP } = this.props;
    const isShowButton = hasCamera && (canCreateMessage || !isCP);
    return isShowButton ? (
      <>
        <Camera
          onMouseDown={async () => {
            const isWebview = getIsOnWebviewApp();
            if (!isWebview) {
              this.cameraInputRef?.click();
              return;
            }

            const permissionStatus = await this.requestWebappCameraPermission();

            // 'denied' means user rejected -> Show notification that SWAG cannot detect camera
            // 'prompt' means asking user to enable -> Show notification about applying user's system settings
            // 'granted' means permission is granted -> run default action
            if (permissionStatus === 'denied') {
              this.props.pushToastr({
                textKey: 'camera_not_authorized',
                color: 'error',
              });
              return;
            } else if (
              permissionStatus === 'prompt' &&
              !this.hasShownCameraPromptNotification
            ) {
              this.hasShownCameraPromptNotification = true;
              this.props.pushToastr({
                textKey: 'camera_permission_default_applied',
                color: 'info',
              });
            }
            this.cameraInputRef?.click();
          }}
        >
          <img src={CameraIcon} alt="photo" />
        </Camera>
        <input
          ref={ref => (this.cameraInputRef = ref)}
          data-element_id={ButtonId.Chatroom.ButtonCamera}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onClick={() => {
            this.editor.blur();
          }}
          onChange={this.handleCameraChange}
        />
      </>
    ) : null;
  }
  renderSendButton() {
    const { isVisibleMenuBtnWhenInputEmpty, text, isFocused, type, media } =
      this.props;

    const Submit = (
      <InputButton
        type="submit"
        aria-label="submit"
        data-element_id={ButtonId.All.ButtonMessageSend}
        data-tracking_payload={{
          'input.type': type,
          'input.text': text,
          'input.media.type': media?.type,
          'input.media.name': media?.name,
          'input.media.size': media?.fileBlob?.size,
        }}
        onMouseDown={e => e.preventDefault()}
        onClick={this.handleSubmit}
      >
        <img src={SendIcon} alt="send icon" />
      </InputButton>
    );

    if (isVisibleMenuBtnWhenInputEmpty) {
      return text.length || isFocused ? Submit : <div></div>;
    }
    return Submit;
  }

  // use debounce to prevent calculate the input height every key press
  setMessageInputHeightDebounce = _.debounce(() => {
    const { setMessageInputHeight } = this.props;
    setMessageInputHeight({
      height: this.messageInputRef?.clientHeight,
      chatroomId: this.chatroomId,
    });
  }, 200);

  handleCameraChange = async ({ target }) => {
    const { text, onChange } = this.props;
    let file = target.files[0];
    const isImage = /^image\//.test(file.type);
    if (isImage) {
      try {
        file = await getMediaBlob(file);
        // eslint-disable-next-line no-empty
      } catch (_) {}
    }
    // const blob = isImage ? await getMediaBlob(file) : file;
    this.handleFocus();
    onChange({
      text,
      media: {
        type: file.type,
        name: file.name,
        fileBlob: file,
      },
    });
    target.value = null;
  };

  handleFocus = () => {
    const { setGiftPanel, setInputFocused, isFocused } = this.props;
    if (!isFocused) {
      setGiftPanel(false);
    }
    setInputFocused(true);
    this.props.onFocus();
    this.editor.focus();
    this.setMessageInputHeightDebounce();
  };

  handleBlur = () => {
    this.setMessageInputHeightDebounce();
    this.props.onBlur();
    this.props.setInputFocused(false);
  };

  /**
   * @param {ClipboardEvent} event
   */
  handlePaste = event => {
    const { clipboardData } = event;
    for (const item of clipboardData.items) {
      if (item.type.startsWith('image')) {
        const file = item.getAsFile();
        getMediaBlob(file).then(async blob => {
          this.handleFocus();
          this.props.onChange({
            text: this.props.text,
            media: {
              type: blob.type,
              name: file.name,
              fileBlob: blob,
            },
          });
        });
      }
    }
  };

  handleChange = ({ value }) => {
    this.setMessageInputHeightDebounce();
    const { media } = this.props;
    this.props.onChange({ text: value, media });
  };

  handleSubmit = e => {
    const {
      media,
      onSubmit,
      type,
      setInputFocused,
      text,
      isFocused,
      pushToastr,
      forbiddenWords,
    } = this.props;

    e.preventDefault();

    if (text || media) {
      const matchWordsGroup = matchFilterWords({
        value: text,
        filterRefString: forbiddenWords,
      });
      if (matchWordsGroup !== null) {
        pushToastr({
          textKey: 'cannot_input_illegal_wording',
          textKeyOption: {
            ['0']: matchWordsGroup?.[0],
            ns: TranslationNamespace.CHAT,
          },
        });
        return false;
      } else {
        onSubmit({ text, media });
      }
    }

    if (type === 'chatroom' && isFocused) {
      this.editor.focus();
      setInputFocused(true);
    }
  };

  handleAudioChannelClick = async () => {
    const {
      directToChatByAudio,
      setChatAudioPanel,
      hasEnoughBalance,
      isAuthed,
      hasPurchased,
      addvalue,
      openQuickpayModal,
      pushToastr,
      openMicrophoneNotSupportModal,
      login,
    } = this.props;

    if (directToChatByAudio) {
      directToChatByAudio();
    } else {
      if (!navigator.mediaDevices) {
        openMicrophoneNotSupportModal();
      } else {
        if (!isAuthed && hasEnoughBalance) {
          await login();
          setChatAudioPanel(true);
        } else if (!isAuthed || (!hasPurchased && !hasEnoughBalance)) {
          openQuickpayModal({ sectionId: QUICKPAY_BUNDLE_SECTION.CHAT });
        } else if (hasEnoughBalance) {
          setChatAudioPanel(true);
        } else {
          addvalue({ via: 'chat-voice-message' });
          pushToastr({ textKey: 'alert_insufficient_funds' });
        }
      }
    }
  };

  render() {
    const {
      type,
      isLoading,
      hasGift,
      text,
      media,
      cost,
      shouldAutoFocus,
      isGiftPanelOpen,
      setGiftPanel,
      directToChatGift,
      isFocused,
      showAudio,
    } = this.props;
    const hasMedia = !!media;
    return (
      <StyledMessageInput>
        <MessageInputContainer>
          {showAudio && (
            <AudioChannelButton
              data-element_id={ButtonId.All.ButtonRecord}
              aria-label="record messages"
              isOnChatroom={type === 'chatroom'}
              onClick={() => this.handleAudioChannelClick()}
            >
              <img src={AudioIcon} alt="audio icon" />
            </AudioChannelButton>
          )}
          <InputContainer
            ref={ref => (this.messageInputRef = ref)}
            isFocused={isFocused}
            isOnChatroom={type === 'chatroom'}
          >
            {hasMedia && this.renderMediaPreview()}
            <InputWrapper onSubmit={this.handleSubmit}>
              {this.renderCameraButton()}
              <InputContent>
                {this.renderPlaceholder()}
                <Editor
                  hasText={!!text.length}
                  isFocused={isFocused}
                  hasPadding={cost > 0 || (type === 'chatroom' && cost > 0)}
                >
                  <TextEditor
                    ref={ref => (this.editor = ref)}
                    isDisabled={isLoading}
                    rows="1"
                    mode="chat"
                    value={text}
                    maxLength={MESSAGE_TEXT_LIMIT}
                    shouldAutoFocus={shouldAutoFocus}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    onKeyUp={() => this.setMessageInputHeightDebounce()}
                    onPaste={this.handlePaste}
                  />
                </Editor>
                {this.renderTextPreview()}
              </InputContent>
              <LoadingContainer>
                {isLoading && <Loading src={LoadingImage} alt="loading" />}
                {this.renderSendButton()}
              </LoadingContainer>
            </InputWrapper>
          </InputContainer>
          {hasGift ? (
            <GiftChannelButton
              aria-label={`${isGiftPanelOpen ? 'collapse' : 'expand'} gift menu`}
              data-element_id={ButtonId.All.ButtonGift}
              isActive={isGiftPanelOpen}
              isOnChatroom={type === 'chatroom'}
              onClick={() => {
                directToChatGift
                  ? directToChatGift()
                  : setGiftPanel(!isGiftPanelOpen);
              }}
            >
              <img src={GiftIcon} alt="gift menu toggle" />
            </GiftChannelButton>
          ) : null}
        </MessageInputContainer>
      </StyledMessageInput>
    );
  }
}

MessageInput.propTypes = {
  type: PropTypes.oneOf(['message', 'chatroom']),
  forbiddenWords: PropTypes.string,
  cost: PropTypes.number,
  text: PropTypes.string,
  media: PropTypes.object,
  hasCamera: PropTypes.bool,
  showAudio: PropTypes.bool,
  hasGift: PropTypes.bool,
  canCreateMessage: PropTypes.bool,
  isCP: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFocused: PropTypes.bool,
  isGiftPanelOpen: PropTypes.bool,
  hasEnoughBalance: PropTypes.bool,
  isAuthed: PropTypes.bool,
  hasPurchased: PropTypes.bool,
  isVisibleMenuBtnWhenInputEmpty: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  addvalue: PropTypes.func,
  openQuickpayModal: PropTypes.func,
  setGiftPanel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  directToChatGift: PropTypes.func,
  setMessageInputHeight: PropTypes.func,
  onFocus: PropTypes.func,
  setChatAudioPanel: PropTypes.func,
  onBlur: PropTypes.func,
  setInputFocused: PropTypes.func,
  pushToastr: PropTypes.func,
  directToChatByAudio: PropTypes.func,
  openMicrophoneNotSupportModal: PropTypes.func,
  t: PropTypes.func.isRequired,
  login: PropTypes.func,
};

MessageInput.defaultProps = {
  type: null,
  forbiddenWords: '',
  cost: 0,
  text: '',
  showAudio: true,
  media: null,
  hasCamera: true,
  hasGift: true,
  canCreateMessage: false,
  isCP: false,
  isGiftPanelOpen: false,
  isLoading: false,
  isVisibleMenuBtnWhenInputEmpty: false,
  shouldAutoFocus: false,
  hasEnoughBalance: false,
  isAuthed: false,
  hasPurchased: false,
  directToChatGift: null,
  directToChatByAudio: null,
  addvalue: () => null,
  openQuickpayModal: () => null,
  setChatAudioPanel: () => null,
  setGiftPanel: () => null,
  onChange: () => null,
  onFocus: () => null,
  onBlur: () => null,
  onSubmit: () => null,
  setInputFocused: () => null,
  setMessageInputHeight: () => null,
  openMicrophoneNotSupportModal: () => null,
  pushToastr: () => null,
  login: () => null,
};

const StyledMessageInput = styled.div`
  position: relative;
  width: auto;
`;

const MediaPreview = styled.ul`
  display: flex;
  align-items: center;
  height: 104px;
  margin: 0 12px;
  border-bottom: 1px solid
    ${({ isOnChatroom }) => (isOnChatroom ? '#323232' : '#64646477')};
  padding: 0;
`;

const Media = styled.li`
  position: relative;
  list-style: none;
`;

const MediaRemover = styled.button`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -8px;
  right: -8px;
  outline: none;
  width: 20px;
  height: 20px;
  border: 0px;
  border-radius: 10px;
  background-color: white;
  padding: 4px;
  z-index: 1;
  cursor: pointer;

  & > img {
    width: 100%;
    height: 100%;
  }
`;

const PreviewWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
`;

const Camera = styled.label`
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  margin-left: 12px;
  height: 40px;
  cursor: pointer;

  & > img {
    height: 32px;
  }
`;

const Editor = styled.div`
  z-index: 1;
  flex: auto;
  position: relative;
  bottom: 0;
  padding-top: ${({ isFocused, hasText, hasPadding }) =>
    (hasText || isFocused) && hasPadding ? '25px' : 0};
  width: 1px;
  color: #fff;
  caret-color: #fff;
  padding-bottom: ${({ hasPadding }) => (hasPadding ? '8px' : '1px')};
`;

// Refs: https://davidwalsh.name/css-ellipsis-left
const TextPreview = styled.div`
  position: absolute;
  flex: auto;
  width: 100%;
  height: 17px;
  white-space: nowrap;
  padding-top: 25px;
  overflow: hidden;
  text-overflow: ellipsis;
  direction: rtl;
  line-height: 17px;
  text-align: left;
`;

const Loading = styled.img`
  position: relative;
  width: 22px;
  height: 22px;
  animation: 3s infinite linear ${Spinning};
`;

const MessageInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const AudioChannelButton = styled.button`
  margin-right: 8px;
  width: 40px;
  height: 40px;
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ isOnChatroom }) =>
    isOnChatroom ? 'rgba(100, 100, 100, 0.15)' : 'none'};
  outline: none;
  border: solid 1px rgba(100, 100, 100, 0.5);
  cursor: pointer;
  img {
    width: 24px;
  }
`;

const GiftChannelButton = styled.button`
  width: 40px;
  height: 40px;
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: none;
  outline: none;
  cursor: pointer;
  margin-left: 12px;
  border: none;
  background-image: linear-gradient(135deg, #00d2be, #5d82bc 52%, #8e37d7);
  & > img {
    height: 24px;
  }
`;

const InputContainer = styled.div`
  position: relative;
  height: auto;
  width: 100%;
  border-radius: 20px;
  ${({ isOnChatroom }) =>
    isOnChatroom
      ? `
        border: solid 1px rgba(100, 100, 100, 0.5);
        background-color: rgba(100, 100, 100, 0.15);
      `
      : `
        border: solid 1px rgba(255, 255, 255, 0.4);
        background-color: none;
      `}
`;

const InputWrapper = styled.form`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
`;

const InputContent = styled.div`
  position: relative;
  width: 100%;
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 12px;
  min-height: 40px;
  font-size: 16px;
  line-height: 20px;
`;

const InputButton = styled.button`
  z-index: 1;
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  border: 0;
  padding: 0;
  margin: 0 8px;
  width: auto;
  height: 40px;
  background: none;
  cursor: pointer;

  & > img {
    width: 24px;
  }
`;

const Placeholder = styled.div`
  position: absolute;
  ${({ isFocused }) => isFocused && 'top: 5px;'};
  color: ${({ textColor }) => textColor};
  font-size: ${({ isFocused }) => (isFocused ? '12px' : '14px')};
  transition: top 0.3s ease;
`;

const Diamond = styled.img`
  vertical-align: bottom;
  height: 16px;
  padding: 0 2px;
  transform: translateY(-2px);
`;

const ReplayPrice = styled.span`
  color: ${textColor['primary']};
`;

const LoadingContainer = styled.div`
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: auto;
  height: 40px;
`;

const Preview = styled.div`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export default withTranslation()(MessageInput);
